import { Component } from 'react'
import { Alert } from 'reactstrap'
import IncidentList from './IncidentsList'
import IncidentSubNav from './IncidentSubNav'
import './Incidents.css'

// Columns most useful for spotting related/duplicate incidents.
const RELATED_INCIDENTS_COLUMNS = [
    { id: "id", name: "Date Added", sortable: true, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "title", name: "Incident Name", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "incidentDate", name: "Incident Date", sortable: true, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "incidentTime", name: "Incident Time", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "status", name: "Status", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "source", name: "Source", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "driver__id", name: "Driver", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "car__id", name: "Car", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "hasLiabilityClaim", name: "Liability Claim?", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "hasSubroClaim", name: "Subro Claim?", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
    { id: "isArchived", name: "Archived?", sortable: false, preferenceSettings: { isVisible: true, canToggleVisibility: false, canReorder: false } },
].map((column, index) => ({ ...column, order: index }))

const VIEWS = {
    DUPLICATES: "duplicates",
    RELATED: "related",
}

class RelatedIncidentsTab extends Component {
    constructor(props) {
        super(props)
        this.state = {
            view: VIEWS.DUPLICATES,
            orderBy: ["-incidentDate"],
            selectedIncidents: [],
            showBanner: true,
        }
    }

    updateOrder = (string) => {
        let order = this.state.orderBy ? [...this.state.orderBy] : []
        let index = order.indexOf(string)
        if (index > -1) {
            order[index] = "-" + string
        } else {
            index = order.indexOf("-" + string)
            index > -1 ? order.splice(index, 1) : order.unshift(string)
        }
        this.setState({ orderBy: order })
    }

    setView = (view) => {
        if (view !== this.state.view) {
            this.setState({ view, selectedIncidents: [] })
        }
    }

    renderDuplicatesView = () => {
        if (!this.props.carId) {
            return <Alert color="info" className="mt-3">This incident has no car attached, so potential duplicates cannot be looked up.</Alert>
        }
        if (!this.props.incidentDate) {
            return <Alert color="info" className="mt-3">This incident has no date, so potential duplicates on the same day cannot be looked up.</Alert>
        }
        return (
            <div className="mt-3">
                <Alert color="info">
                    Incidents below belong to the <strong>same car on the same day</strong> — likely duplicates. Open any incident to archive, delete, or transfer its data.
                </Alert>
                <IncidentList
                    columnConfig={RELATED_INCIDENTS_COLUMNS}
                    currentUser={this.props.currentUser}
                    carId={this.props.carId}
                    incidentDate={this.props.incidentDate}
                    excludeIncidentIds={this.props.excludeIncidentIds}
                    transferTargetIncident={this.props.currentIncident}
                    orderBy={this.state.orderBy}
                    updateOrder={this.updateOrder}
                    selectedIncidents={this.state.selectedIncidents}
                    setSelectedIncidents={(selectedIncidents) => this.setState({ selectedIncidents })}
                />
            </div>
        )
    }

    renderRelatedView = () => {
        const vins = this.props.otherVehicleVins || []
        if (!this.props.incidentDate) {
            return <Alert color="info" className="mt-3">This incident has no date, so related incidents on the same day cannot be looked up.</Alert>
        }
        if (!vins.length) {
            return <Alert color="info" className="mt-3">No other vehicle parties with a VIN are recorded on this incident, so related incidents cannot be looked up.</Alert>
        }
        return (
            <div className="mt-3">
                <Alert color="info">
                    Incidents below involve the <strong>other vehicle(s) from this incident</strong> (matched by VIN) on the <strong>same day</strong>.
                </Alert>
                <IncidentList
                    columnConfig={RELATED_INCIDENTS_COLUMNS}
                    currentUser={this.props.currentUser}
                    carVins={vins}
                    incidentDate={this.props.incidentDate}
                    excludeIncidentIds={this.props.excludeIncidentIds}
                    orderBy={this.state.orderBy}
                    updateOrder={this.updateOrder}
                    selectedIncidents={this.state.selectedIncidents}
                    setSelectedIncidents={(selectedIncidents) => this.setState({ selectedIncidents })}
                />
            </div>
        )
    }

    render() {
        const { view } = this.state
        const duplicatesCount = this.props.duplicatesCount || 0
        const relatedCount = this.props.relatedCount || 0
        const subNavItems = [
            { id: VIEWS.DUPLICATES, label: "Duplicates", icon: "fa-clone", count: this.props.duplicatesCount ?? null },
            { id: VIEWS.RELATED, label: "Related", icon: "fa-link", count: this.props.relatedCount ?? null },
        ]
        const showBanner = this.state.showBanner && (duplicatesCount > 0 || relatedCount > 0)
        return (
            <div className="related-tab-container">
                {showBanner && (
                    <div className="incident-overview-banner">
                        <span className="incident-overview-banner__icon"><i className="fa fa-info" aria-hidden="true" /></span>
                        <div className="incident-overview-banner__text">
                            <div className="incident-overview-banner__title">Potential Duplicate Records Detected</div>
                            <div className="incident-overview-banner__desc">
                                Our system has identified {duplicatesCount} prospective duplicate{duplicatesCount === 1 ? "" : "s"} and {relatedCount} linked historical record{relatedCount === 1 ? "" : "s"} with shared drivers, vehicle parameters, and overlapping incident timelines. Please review below.
                            </div>
                        </div>
                        <button type="button" className="incident-overview-banner__close" onClick={() => this.setState({ showBanner: false })} aria-label="Dismiss">
                            <i className="fa fa-times-circle" aria-hidden="true" />
                        </button>
                    </div>
                )}
                <div className="incident-overview-header">
                    <div className="incident-overview-header__titles">
                        <h3 className="incident-overview-header__title">Related &amp; Duplicates Overview</h3>
                        <p className="incident-overview-header__subtitle">Analyze, confirm, or archive identified collision cross-references</p>
                    </div>
                    <IncidentSubNav items={subNavItems} activeId={view} onSelect={this.setView} />
                </div>
                {view === VIEWS.DUPLICATES ? this.renderDuplicatesView() : this.renderRelatedView()}
            </div>
        )
    }
}

export default RelatedIncidentsTab
